import { Link } from 'react-router-dom'
import { Menu, HelpCircle, LogOut, Settings, User } from 'lucide-react'
import { useAuth } from '@usoftware/auth-react'
import { cn } from '@/lib/utils'
import { useOrg } from '@/contexts'
import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui'

interface TopNavProps {
  onMenuClick?: () => void
  showMenuButton?: boolean
  className?: string
}

export function TopNav({ onMenuClick, showMenuButton = false, className }: TopNavProps) {
  const { name: orgName, branding } = useOrg()

  return (
    <header
      className={cn(
        'flex h-14 items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--bg-base)] px-4',
        className
      )}
    >
      {/* Left side */}
      <div className="flex items-center gap-3">
        {showMenuButton && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="lg:hidden"
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle menu</span>
          </Button>
        )}

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={orgName || 'Logo'}
              className="h-8 w-auto"
            />
          ) : (
            <span className="text-lg font-semibold text-[var(--text-primary)]">
              {orgName || 'u.software'}
            </span>
          )}
        </Link>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Help */}
        <Button
          variant="ghost"
          size="icon"
          asChild
        >
          <a
            href="https://docs.u.software"
            target="_blank"
            rel="noopener noreferrer"
            title="Help"
          >
            <HelpCircle className="h-5 w-5" />
            <span className="sr-only">Help</span>
          </a>
        </Button>

        {/* User menu */}
        <UserMenu />
      </div>
    </header>
  )
}

function UserMenu() {
  const { user, logout } = useAuth()

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'User'
  const email = user?.email || ''
  const avatarUrl = user?.avatarUrl || null
  const userId = user?.id || undefined

  const handleLogout = async () => {
    await logout()
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar
            src={avatarUrl}
            fallback={displayName}
            userId={userId}
            size="sm"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            <p className="text-xs leading-none text-[var(--text-secondary)]">
              {email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account/profile">
            <User className="mr-2 h-4 w-4" />
            <span>Account</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/account/activity">
            <Settings className="mr-2 h-4 w-4" />
            <span>Activity</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
